import { useState, useEffect, useRef } from 'react';
import gibletLogoGreen from '@/assets/giblet-logo-green.png';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Clock, Send, Loader2, MessageSquare, History } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { useProfile } from '@/hooks/useProfile';
import { callGibletApi, ThreadPurpose, GibletBackend } from '@/services/gibletService';
import { KBConnectionPanel } from './KBConnectionPanel';
import { KBChip } from './KBChip';
import { useConnectedKBs } from '@/hooks/useConnectedKBs';
import { useKnowledgeBases, useAccessibleKnowledgeBases } from '@/hooks/useKnowledgeBases';
import { toast } from 'sonner';
import { stripMarkdownFormatting } from '@/lib/stripMarkdown';
import { GibletQuestionForm } from './GibletQuestionForm';
import { SelectionButtons } from './SelectionButtons';
import { GibletChatHistory } from './GibletChatHistory';
import { GibletChronologicalHistory } from './GibletChronologicalHistory';
import { supabase } from '@/integrations/supabase/client';
import { CitationList } from './CitationList';
import { MessageFeedback } from './MessageFeedback';
import { ZoomControls } from './ZoomControls';
import { CallCreationInlineForm } from './CallCreationInlineForm';
import { useCallCreationFlow, CallType } from '@/hooks/useCallCreationFlow';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  CallTypeMessage,
  FunctionalAreaMessage,
  BountyMessage,
  BountyDetailsMessage,
  CallDetailsMessage,
  RolesMessage,
  ReviewMessage,
} from './CallCreationChatMessages';

interface Citation {
  kbTitle: string;
  kbDescription?: string;
  snippet: string;
  relevance: number;
}

interface Message {
  role: 'user' | 'assistant';
  content: string;
  citations?: Citation[];
}

const getGibletVariant = (pathname: string): string => {
  return 'Giblet Chat';
};

export const GibletChatPanel = ({ initialCallType }: { initialCallType?: 'solution' | 'knowledge' | 'destruction' | 'start-call-flow' | null }) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showThreadHistory, setShowThreadHistory] = useState(false);
  const [showChronological, setShowChronological] = useState(false);
  const [submittedFormMessageIdx, setSubmittedFormMessageIdx] = useState<number | null>(null);
  const [workflowState, setWorkflowState] = useState<Record<string, any>>({});
  const [selectedMessageIdx, setSelectedMessageIdx] = useState<number | null>(null);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [userId, setUserId] = useState<string | undefined>();
  const [zoomLevel, setZoomLevel] = useState(100);
  const [showCallForm, setShowCallForm] = useState(false);
  const [preselectedCallType, setPreselectedCallType] = useState<'solution' | 'knowledge' | 'destruction' | null>(null);
  const [isDraftConversation, setIsDraftConversation] = useState(true);
  const [isCreatingCall, setIsCreatingCall] = useState(false);
  const [threadPurpose, setThreadPurpose] = useState<ThreadPurpose>('general');
  const [gibletBackend, setGibletBackend] = useState<GibletBackend>(() => {
    // Load saved preference from localStorage
    const saved = localStorage.getItem('giblet-backend');
    return (saved === 'openai-direct' ? 'openai-direct' : 'lovable-ai') as GibletBackend;
  });
  
  const callFlow = useCallCreationFlow(preselectedCallType);
  const scrollRef = useRef<HTMLDivElement>(null);
  
  const location = useLocation();
  const { profile } = useProfile();
  const { data: allKBs } = useKnowledgeBases();
  
  useEffect(() => {
    supabase.auth.getUser().then(({ data: { user } }) => {
      setUserId(user?.id);
      if (user?.id) {
        loadOrCreateConversation(user.id);
      }
    });
  }, []);
  
  const { data: accessibleKBs } = useAccessibleKnowledgeBases(userId);
  const maxConnectedKBs = accessibleKBs?.length || 50;
  
  const {
    connectedKBs,
    connectKB,
    disconnectKB,
    canConnectMore,
    getRecentlyUsed
  } = useConnectedKBs(conversationId, maxConnectedKBs);

  const variant = getGibletVariant(location.pathname);
  const variantKey = 'chat';

  useEffect(() => {
    scrollRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Handle initialCallType from dropdown or megaphone button
  useEffect(() => {
    if (initialCallType && !isCreatingCall) {
      // Set thread purpose for call creation
      setThreadPurpose('call_creation');
      
      if (initialCallType === 'start-call-flow') {
        // Start from Call Type selection
        setIsCreatingCall(true);
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: 'call-flow-type',
        }]);
      } else {
        // Pre-selected call type, skip to functional area
        setPreselectedCallType(initialCallType);
        setIsCreatingCall(true);
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: 'call-flow-functional-area',
        }]);
      }
    }
  }, [initialCallType, isCreatingCall]);

  // Detect thread purpose based on context
  const detectThreadPurpose = (): ThreadPurpose => {
    // If actively creating a call, use call_creation
    if (isCreatingCall || initialCallType) return 'call_creation';
    
    // If KBs are connected, likely kb_work
    if (connectedKBs.length > 0) return 'kb_work';
    
    // Check page context for team-related pages
    const pathname = location.pathname;
    if (pathname.includes('/channel') || pathname.includes('/team') || pathname.includes('/network')) {
      return 'team_collaboration';
    }
    
    // Default to general
    return 'general';
  };

  const loadOrCreateConversation = async (uid: string) => {
    try {
      // If starting a call flow, skip loading existing conversation
      // and start fresh with a new draft thread
      if (initialCallType) {
        setConversationId(null);
        setIsDraftConversation(true);
        // Don't reset messages - let the call flow useEffect handle it
        return;
      }

      // Try to get active conversation for this variant
      const { data: activeConv, error: activeError } = await supabase
        .from('giblet_conversations')
        .select('*')
        .eq('user_id', uid)
        .eq('giblet_variant', variantKey)
        .eq('is_active', true)
        .maybeSingle();

      if (activeError) throw activeError;

      if (activeConv) {
        // Load existing conversation
        setConversationId(activeConv.id);
        setIsDraftConversation(false);
        await loadConversationMessages(activeConv.id);
      } else {
        // Set up draft state - NO DATABASE INSERT
        setConversationId(null);
        setMessages([]);
        setIsDraftConversation(true);
      }
    } catch (error: any) {
      console.error('Error loading conversation:', error);
    }
  };

  const createNewConversation = async (uid: string) => {
    try {
      const { data, error } = await supabase
        .from('giblet_conversations')
        .insert({
          user_id: uid,
          title: 'New Conversation',
          giblet_variant: variantKey,
          page_context: location.pathname,
          is_active: true,
        })
        .select()
        .single();

      if (error) throw error;
      setConversationId(data.id);
      setMessages([]);
    } catch (error: any) {
      console.error('Error creating conversation:', error);
    }
  };

  const loadConversationMessages = async (convId: string) => {
    try {
      const { data, error } = await supabase
        .from('giblet_chat_messages')
        .select('*')
        .eq('conversation_id', convId)
        .order('created_at', { ascending: true });

      if (error) throw error;

      const loadedMessages: Message[] = (data || []).map(msg => ({
        role: msg.role as 'user' | 'assistant',
        content: msg.content,
      }));

      setMessages(loadedMessages);
    } catch (error: any) {
      console.error('Error loading messages:', error);
    }
  };

  const saveMessage = async (message: Message) => {
    if (!conversationId || !userId) return;

    try {
      await supabase
        .from('giblet_chat_messages')
        .insert({
          conversation_id: conversationId,
          user_id: userId,
          role: message.role,
          content: message.content,
          connected_kb_ids: connectedKBs,
          workflow_state: workflowState,
        });

      // Update conversation last_message_at and title if first message
      const updates: any = { last_message_at: new Date().toISOString() };
      
      if (messages.length === 0 && message.role === 'user') {
        // Auto-generate title from first user message
        const title = message.content.slice(0, 50) + (message.content.length > 50 ? '...' : '');
        updates.title = title;
      }

      await supabase
        .from('giblet_conversations')
        .update(updates)
        .eq('id', conversationId);
    } catch (error: any) {
      console.error('Error saving message:', error);
    }
  };

  const handleSelectConversation = async (convId: string) => {
    if (!userId) return;

    try {
      // If current is a draft (unsaved), just discard it
      if (isDraftConversation) {
        // No database cleanup needed - it was never saved
        setIsDraftConversation(false);
      } else if (conversationId) {
        // Mark previous saved conversation as not active
        await supabase
          .from('giblet_conversations')
          .update({ is_active: false })
          .eq('id', conversationId);
      }

      // Mark selected as active
      await supabase
        .from('giblet_conversations')
        .update({ is_active: true })
        .eq('id', convId);

      setConversationId(convId);
      setIsDraftConversation(false);
      await loadConversationMessages(convId);
      setShowThreadHistory(false);
    } catch (error: any) {
      console.error('Error selecting conversation:', error);
      toast.error('Failed to load conversation');
    }
  };

  const handleNewConversation = async () => {
    if (!userId) return;

    try {
      // If current is draft, just reset state
      if (isDraftConversation) {
        setMessages([]);
        setInput('');
        setShowThreadHistory(false);
        return;
      }
      
      // Mark current conversation as not active
      if (conversationId) {
        await supabase
          .from('giblet_conversations')
          .update({ is_active: false })
          .eq('id', conversationId);
      }

      // Set up new draft state
      setConversationId(null);
      setMessages([]);
      setIsDraftConversation(true);
      setShowThreadHistory(false);
    } catch (error: any) {
      console.error('Error creating new conversation:', error);
      toast.error('Failed to create new conversation');
    }
  };

  const buildUserContext = () => {
    const pageName = location.pathname;
    let context = `Current page: ${pageName}`;
    
    if (profile) {
      context += `\nUser: ${profile.display_name}`;
      if (profile.organization) context += `\nOrganization: ${profile.organization}`;
      if (profile.title) context += `\nTitle: ${profile.title}`;
    }
    
    return context;
  };

  // Call type abbreviations for thread titles
  const CALL_TYPE_ABBR: Record<string, string> = {
    'solution': 'CfS',
    'knowledge': 'CfK',
    'destruction': 'CfD'
  };

  const handleCallFormComplete = async (formData: any) => {
    setShowCallForm(false);
    
    // Generate structured title for the new Call thread
    const callTypeAbbr = CALL_TYPE_ABBR[formData.type.toLowerCase()] || 'Call';
    const functionalArea = formData.functionalArea.substring(0, 3).toUpperCase();
    const topic = formData.title.length > 30 ? formData.title.substring(0, 30) + '...' : formData.title;
    const threadTitle = `${callTypeAbbr}: ${functionalArea} - ${topic}`;
    
    if (!userId) return;
    
    try {
      // Mark current conversation as not active
      if (conversationId) {
        await supabase
          .from('giblet_conversations')
          .update({ is_active: false })
          .eq('id', conversationId);
      }
      
      // Create NEW conversation for this Call
      const { data: newConv, error: convError } = await supabase
        .from('giblet_conversations')
        .insert({
          user_id: userId,
          title: threadTitle,
          giblet_variant: variantKey,
          page_context: location.pathname,
          is_active: true,
          metadata: {
            call_type: formData.type,
            functional_area: formData.functionalArea,
            call_topic: formData.title,
            is_call_thread: true,
            bounty_amount: formData.isBounty ? formData.bountyAmount : null
          }
        })
        .select()
        .single();
      
      if (convError) throw convError;
      
      setConversationId(newConv.id);
      setIsDraftConversation(false);
      setMessages([]);
      
      // Create a formatted summary for Giblet AI to review
      const callSummary = `I'd like to create a Call for ${formData.type}:

**Type:** ${formData.type}
**Functional Area:** ${formData.functionalArea}
**Title:** ${formData.title}
**Bounty:** ${formData.isBounty ? `$${formData.bountyAmount} (Deadline: ${formData.deadline})` : 'None'}

**Description:**
${formData.description}

**Requirements:**
${formData.requirements}

**Roles:**
- Curator: ${formData.curator}
- Confirmer: ${formData.confirmer}
${formData.contributors.length > 0 ? `- Contributors: ${formData.contributors.join(', ')}` : ''}

Please review this call and help me finalize it. Can you suggest any improvements or confirm it's ready to submit?`;

      const userMessage: Message = {
        role: 'user',
        content: callSummary
      };
      
      setMessages([userMessage]);
      await saveMessage(userMessage);
      
      setIsLoading(true);
      
      // Get Giblet's response
      const result = await callGibletApi(
        callSummary,
        buildUserContext(),
        [],
        connectedKBs,
        workflowState,
        undefined,
        'call_creation',
        gibletBackend
      );
      
      const assistantMessage: Message = {
        role: 'assistant',
        content: result.response,
        citations: result.citations
      };
      
      setMessages([userMessage, assistantMessage]);
      await saveMessage(assistantMessage);
      
      toast.success(`New Call thread created: ${threadTitle}`);
    } catch (error) {
      console.error('Error creating Call thread:', error);
      toast.error('Failed to create Call thread');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCallFormCancel = () => {
    setShowCallForm(false);
    setPreselectedCallType(null);
  };

  const handleCallTypeSelect = (type: CallType) => {
    callFlow.updateFormData('type', type);
    const typeLabel = type === 'solution' ? 'Solution' : type === 'knowledge' ? 'Knowledge' : 'Destruction';
    setMessages(prev => [
      ...prev,
      { role: 'user', content: `Call for ${typeLabel}` },
      { role: 'assistant', content: 'call-flow-functional-area' }
    ]);
    callFlow.progressToNextStep();
  };

  // Detect if user is confirming call creation intent
  const detectCallCreationIntent = (message: string, conversationHistory: Message[]): { shouldTriggerFlow: boolean; callType?: CallType } => {
    const lowerMessage = message.toLowerCase().trim();
    
    // Check recent assistant messages for call creation offers
    const recentAssistantMessages = conversationHistory
      .slice(-4)
      .filter(m => m.role === 'assistant')
      .map(m => m.content.toLowerCase());
    
    const hadCallCreationOffer = recentAssistantMessages.some(content => 
      content.includes('call for solution') || 
      content.includes('call for knowledge') || 
      content.includes('call for destruction') ||
      content.includes('create a call') ||
      content.includes('setting up a call')
    );
    
    if (!hadCallCreationOffer) return { shouldTriggerFlow: false };
    
    // Check if user is confirming
    const confirmationPatterns = [
      /^yes/i, /^yeah/i, /^yep/i, /^sure/i, /^ok/i, /^okay/i, /^let'?s do it/i,
      /^let'?s go/i, /^sounds good/i, /^go ahead/i, /^please/i, /^definitely/i,
      /^absolutely/i, /^create it/i, /^start it/i, /^proceed/i
    ];
    
    const isConfirming = confirmationPatterns.some(pattern => pattern.test(lowerMessage));
    
    if (isConfirming) {
      // Determine call type from context
      const contextText = recentAssistantMessages.join(' ');
      let callType: CallType = 'solution';
      if (contextText.includes('call for knowledge')) callType = 'knowledge';
      else if (contextText.includes('call for destruction')) callType = 'destruction';
      
      return { shouldTriggerFlow: true, callType };
    }
    
    // Also detect direct requests
    if (lowerMessage.includes('create') && lowerMessage.includes('call')) {
      let callType: CallType = 'solution';
      if (lowerMessage.includes('knowledge')) callType = 'knowledge';
      else if (lowerMessage.includes('destruction')) callType = 'destruction';
      return { shouldTriggerFlow: true, callType };
    }
    
    return { shouldTriggerFlow: false };
  };

  const handleSend = async (overrideInput?: string) => {
    const messageText = overrideInput || input;
    if (!messageText.trim() || isLoading) return;

    // Check for call creation intent BEFORE sending to AI
    const callIntent = detectCallCreationIntent(messageText, messages);
    
    if (callIntent.shouldTriggerFlow && !isCreatingCall) {
      // User confirmed call creation - trigger inline form flow instead of text response
      setInput('');
      setThreadPurpose('call_creation');
      setIsCreatingCall(true);
      
      // Add user's confirmation message
      const userMessage: Message = { role: 'user', content: messageText };
      setMessages(prev => [...prev, userMessage]);
      
      // If we detected a specific call type, skip to functional area
      if (callIntent.callType) {
        setPreselectedCallType(callIntent.callType);
        callFlow.updateFormData('type', callIntent.callType);
        setMessages(prev => [...prev, { role: 'assistant', content: 'call-flow-functional-area' }]);
      } else {
        // Start from type selection
        setMessages(prev => [...prev, { role: 'assistant', content: 'call-flow-type' }]);
      }
      return;
    }

    // If this is a draft conversation, create it NOW
    if (isDraftConversation && userId) {
      try {
        const { data, error } = await supabase
          .from('giblet_conversations')
          .insert({
            user_id: userId,
            title: 'New Conversation',
            giblet_variant: variantKey,
            page_context: location.pathname,
            is_active: true,
          })
          .select()
          .single();
          
        if (error) {
          console.error('Error creating conversation:', error);
          toast.error('Failed to create conversation');
          return;
        }
        
        setConversationId(data.id);
        setIsDraftConversation(false);
      } catch (error) {
        console.error('Error creating conversation:', error);
        toast.error('Failed to create conversation');
        return;
      }
    }

    const userMessage: Message = { role: 'user', content: messageText };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);

    // Auto-save user message
    await saveMessage(userMessage);

    const updatedWorkflow = { ...workflowState };
    setWorkflowState(updatedWorkflow);

    try {
      const currentThreadPurpose = detectThreadPurpose();
      const result = await callGibletApi(
        messageText,
        buildUserContext(),
        messages,
        connectedKBs,
        updatedWorkflow,
        conversationId || undefined,
        currentThreadPurpose,
        gibletBackend
      );

      const assistantMessage: Message = { 
        role: 'assistant', 
        content: result.response,
        citations: result.citations 
      };
      setMessages(prev => [...prev, assistantMessage]);
      
      // Auto-save assistant message
      await saveMessage(assistantMessage);
    } catch (error) {
      toast.error('Failed to get response from Giblet');
      console.error('Giblet error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFormSubmit = async (answers: Record<string, any>, messageIdx: number) => {
    setSubmittedFormMessageIdx(messageIdx);
    
    const updatedWorkflow = { ...workflowState, ...answers };
    setWorkflowState(updatedWorkflow);
    
    const formattedAnswers = Object.entries(answers)
      .map(([key, value]) => `${key}: ${value}`)
      .join('\n');
    
    const userMessage: Message = { 
      role: 'user', 
      content: `Here are my answers:\n\n${formattedAnswers}` 
    };
    
    setMessages(prev => [...prev, userMessage]);
    await saveMessage(userMessage);
    setIsLoading(true);

    try {
      const currentThreadPurpose = detectThreadPurpose();
      const result = await callGibletApi(
        userMessage.content,
        buildUserContext(),
        messages,
        connectedKBs,
        updatedWorkflow,
        conversationId || undefined,
        currentThreadPurpose,
        gibletBackend
      );

      const assistantMessage: Message = { 
        role: 'assistant', 
        content: result.response,
        citations: result.citations 
      };
      setMessages(prev => [...prev, assistantMessage]);
      await saveMessage(assistantMessage);
    } catch (error) {
      toast.error('Failed to get response from Giblet');
      console.error('Giblet error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const detectSelectableOptions = (content: string): string[] | null => {
    // Detect bulleted options (2+ items with dashes or asterisks)
    const lines = content.split('\n');
    const bulletedLines = lines.filter(line => /^[-*]\s+/.test(line.trim()));
    
    // Show buttons for 2+ bulleted options (lowered threshold for inline selection style)
    if (bulletedLines.length >= 2) {
      return bulletedLines.map(line => line.trim());
    }
    
    // Also check for numbered lists
    const numberedLines = lines.filter(line => /^\d+\.\s+/.test(line.trim()));
    
    if (numberedLines.length >= 2) {
      return numberedLines.map(line => {
        const cleaned = line.trim().replace(/^\d+\.\s+/, '');
        return `- ${cleaned}`;
      });
    }
    
    return null;
  };

  const handleOptionSelect = async (option: string, messageIdx: number) => {
    if (isLoading) return;
    setSelectedMessageIdx(messageIdx);
    const cleanOption = option.split('(')[0].trim();
    
    // Check if selected option matches an accessible KB and auto-connect it
    if (accessibleKBs && accessibleKBs.length > 0) {
      const matchedKB = accessibleKBs.find(kb => {
        const kbTitle = kb.title.toLowerCase();
        const optionLower = cleanOption.toLowerCase();
        return kbTitle === optionLower || 
               kbTitle.includes(optionLower) || 
               optionLower.includes(kbTitle);
      });
      
      if (matchedKB && !connectedKBs.includes(matchedKB.id)) {
        connectKB(matchedKB.id);
        toast.success(`Connected: ${matchedKB.title}`);
      }
    }
    
    // Get the assistant message that presented these options
    const assistantMessage = messages[messageIdx];
    const optionsPresented = detectSelectableOptions(assistantMessage?.content || '');
    
    // Determine decision category based on context
    const determineCategory = (): string => {
      const content = assistantMessage?.content?.toLowerCase() || '';
      if (content.includes('call type') || content.includes('what type')) return 'call_type';
      if (content.includes('domain') || content.includes('functional area')) return 'domain';
      if (content.includes('bounty')) return 'bounty';
      if (content.includes('ready to proceed') || content.includes('confirm')) return 'confirmation';
      if (content.includes('rate') || content.includes('rating')) return 'rating';
      if (content.includes('help with') || content.includes('assist')) return 'intent';
      return 'general';
    };
    
    // Save decision to database for preference learning
    if (userId && optionsPresented) {
      try {
        await supabase
          .from('user_giblet_decisions')
          .insert({
            user_id: userId,
            conversation_id: conversationId,
            decision_category: determineCategory(),
            options_presented: optionsPresented,
            option_selected: cleanOption,
            context: assistantMessage?.content?.substring(0, 500) || null,
          });
        console.log('Decision tracked:', { category: determineCategory(), selected: cleanOption });
      } catch (error) {
        console.error('Error tracking decision:', error);
      }
    }
    
    setTimeout(() => {
      handleSend(cleanOption);
    }, 0);
  };

  const handleFunctionalAreaSelect = (area: string) => {
    callFlow.updateFormData('functionalArea', area);
    const areaLabel = [
      { id: 'technical-architecture', name: 'Technical Architecture' },
      { id: 'web3-integration', name: 'Web3 Integration' },
      { id: 'user-experience', name: 'User Experience (UX/UI)' },
      { id: 'compliance-governance', name: 'Compliance & Governance' },
      { id: 'ai-automation', name: 'AI & Automation' },
      { id: 'knowledge-management', name: 'Knowledge Management' },
      { id: 'branding', name: 'Branding' },
      { id: 'value-proposition', name: 'Value Proposition' }
    ].find(a => a.id === area)?.name || area;
    
    setMessages(prev => [
      ...prev,
      { role: 'user', content: areaLabel },
      { role: 'assistant', content: 'call-flow-bounty' }
    ]);
    callFlow.progressToNextStep();
  };

  const handleBountySelect = (isBounty: boolean) => {
    callFlow.updateFormData('isBounty', isBounty);
    setMessages(prev => [
      ...prev,
      { role: 'user', content: isBounty ? 'Yes, add a bounty' : 'No bounty' },
      { role: 'assistant', content: isBounty ? 'call-flow-bounty-details' : 'call-flow-details' }
    ]);
    callFlow.progressToNextStep();
  };

  const handleBountyDetailsSubmit = (amount: string, deadline: string) => {
    callFlow.updateFormData('bountyAmount', amount);
    callFlow.updateFormData('deadline', deadline);
    setMessages(prev => [
      ...prev,
      { role: 'user', content: `Bounty: $${amount}, Deadline: ${deadline}` },
      { role: 'assistant', content: 'call-flow-details' }
    ]);
    callFlow.progressToNextStep();
  };

  const handleCallDetailsSubmit = (title: string, description: string, requirements: string) => {
    callFlow.updateFormData('title', title);
    callFlow.updateFormData('description', description);
    callFlow.updateFormData('requirements', requirements);
    setMessages(prev => [
      ...prev,
      { role: 'user', content: `Title: ${title}` },
      { role: 'assistant', content: 'call-flow-roles' }
    ]);
    callFlow.progressToNextStep();
  };

  const handleRolesSubmit = (curator: string, confirmer: string, contributors: string[]) => {
    callFlow.updateFormData('curator', curator);
    callFlow.updateFormData('confirmer', confirmer);
    callFlow.updateFormData('contributors', contributors);
    setMessages(prev => [
      ...prev,
      { role: 'user', content: `Curator: ${curator}` },
      { role: 'assistant', content: 'call-flow-review' }
    ]);
    callFlow.progressToNextStep();
  };

  const handleCallReviewConfirm = async () => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('calls')
        .insert({
          title: callFlow.formData.title,
          description: callFlow.formData.description,
          bounty_amount: callFlow.formData.isBounty ? parseFloat(callFlow.formData.bountyAmount) : null,
          deadline: callFlow.formData.deadline || null,
          creator_id: userId!,
          status: 'open',
        })
        .select()
        .single();

      if (error) throw error;

      setMessages(prev => [
        ...prev,
        { role: 'user', content: 'Confirm submission' },
        { role: 'assistant', content: `✅ Your Call has been successfully created! You can view and manage it in the Engage section.` }
      ]);
      
      toast.success('Call created successfully!');
      setIsCreatingCall(false);
      callFlow.reset();
    } catch (error) {
      console.error('Error creating call:', error);
      toast.error('Failed to create call. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCallReviewEdit = () => {
    setMessages(prev => [
      ...prev,
      { role: 'user', content: 'Edit call details' },
      { role: 'assistant', content: 'Let me help you edit. Which section would you like to modify?' }
    ]);
  };

  return (
    <>
      <GibletChatHistory
        open={showThreadHistory}
        onOpenChange={setShowThreadHistory}
        userId={userId || ''}
        currentVariant={variantKey}
        onSelectConversation={handleSelectConversation}
        onNewConversation={handleNewConversation}
      />

      <div className="h-full flex flex-col bg-background border rounded-lg">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <div className="flex items-center gap-2">
            <img 
              src={gibletLogoGreen} 
              alt="Giblet" 
              className="h-8 w-8 rounded-full object-cover"
            />
            <div className="flex flex-col">
              <span className="text-sm text-muted-foreground">{variant}</span>
              <Select
                value={gibletBackend}
                onValueChange={(value: GibletBackend) => {
                  setGibletBackend(value);
                  localStorage.setItem('giblet-backend', value);
                  toast.success(`Switched to ${value === 'openai-direct' ? 'OpenAI GPT-4o' : 'Lovable AI (Gemini)'}`);
                }}
              >
                <SelectTrigger className="h-6 text-xs w-[140px] border-none shadow-none p-0 focus:ring-0">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="lovable-ai" className="text-xs">
                    Gemini 2.5 Flash
                  </SelectItem>
                  <SelectItem value="openai-direct" className="text-xs">
                    OpenAI GPT-4o
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex gap-2">
            <ZoomControls zoomLevel={zoomLevel} onZoomChange={setZoomLevel} />
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowThreadHistory(true)}
              className="gap-2"
            >
              <MessageSquare className="h-4 w-4" />
              Threads
            </Button>
          </div>
        </div>

        {/* Connected KB Chips */}
        {connectedKBs.length > 0 && (
          <div className="flex flex-wrap gap-2 p-2 border-b">
            {connectedKBs.map(kbId => {
              const kb = allKBs?.find(k => k.id === kbId);
              return kb ? (
                <KBChip
                  key={kbId}
                  kbId={kbId}
                  title={kb.title}
                  onRemove={() => disconnectKB(kbId)}
                />
              ) : null;
            })}
          </div>
        )}

        {/* Messages */}
        <ScrollArea className="flex-1 px-4 py-3">
          <div 
            className="space-y-4"
            style={{ 
              transform: `scale(${zoomLevel / 100})`,
              transformOrigin: 'top center',
              minHeight: `${100 / (zoomLevel / 100)}%`
            }}
          >
            {messages.map((msg, idx) => {
            // Handle call flow messages
            if (msg.role === 'assistant' && msg.content.startsWith('call-flow-')) {
              if (msg.content === 'call-flow-type') {
                return (
                  <div key={idx} className="flex justify-start">
                    <CallTypeMessage onSelect={handleCallTypeSelect} disabled={isLoading} />
                  </div>
                );
              }
              if (msg.content === 'call-flow-functional-area') {
                return (
                  <div key={idx} className="flex justify-start">
                    <FunctionalAreaMessage 
                      onSelect={handleFunctionalAreaSelect} 
                      callType={callFlow.formData.type!}
                      disabled={isLoading}
                    />
                  </div>
                );
              }
              if (msg.content === 'call-flow-bounty') {
                return (
                  <div key={idx} className="flex justify-start">
                    <BountyMessage onSelect={handleBountySelect} disabled={isLoading} />
                  </div>
                );
              }
              if (msg.content === 'call-flow-bounty-details') {
                return (
                  <div key={idx} className="flex justify-start">
                    <BountyDetailsMessage onSubmit={handleBountyDetailsSubmit} disabled={isLoading} />
                  </div>
                );
              }
              if (msg.content === 'call-flow-details') {
                return (
                  <div key={idx} className="flex justify-start">
                    <CallDetailsMessage onSubmit={handleCallDetailsSubmit} disabled={isLoading} />
                  </div>
                );
              }
              if (msg.content === 'call-flow-roles') {
                return (
                  <div key={idx} className="flex justify-start">
                    <RolesMessage onSubmit={handleRolesSubmit} disabled={isLoading} />
                  </div>
                );
              }
              if (msg.content === 'call-flow-review') {
                return (
                  <div key={idx} className="flex justify-start">
                    <ReviewMessage 
                      formData={callFlow.formData}
                      onConfirm={handleCallReviewConfirm}
                      onEdit={handleCallReviewEdit}
                      disabled={isLoading}
                    />
                  </div>
                );
              }
            }
            
            // Regular message rendering
            const hasQuestionPrompt = msg.role === 'assistant' && msg.content.includes('QUESTION_PROMPT:');
            const isFormSubmitted = submittedFormMessageIdx === idx;
            
            const displayContent = hasQuestionPrompt 
              ? msg.content.replace(/QUESTION_PROMPT:\s*{[\s\S]*?}\s*(?:\n|$)/, '').trim()
              : msg.content;
            
            const selectableOptions = msg.role === 'assistant' ? detectSelectableOptions(displayContent) : null;
            const hasSelectableOptions = selectableOptions && selectableOptions.length > 0;
            
            const textContent = hasSelectableOptions 
              ? displayContent.split('\n').filter(line => !/^[-*]\s+/.test(line.trim())).join('\n').trim()
              : displayContent;
            
            return (
              <div
                key={idx}
                className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[85%] rounded-lg p-3 ${
                    msg.role === 'user'
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-muted'
                  }`}
                >
                  {textContent && (
                    <div 
                      dangerouslySetInnerHTML={{ __html: stripMarkdownFormatting(textContent) }}
                      className="text-sm leading-relaxed whitespace-pre-wrap break-words"
                    />
                  )}
                  
                  {msg.citations && <CitationList citations={msg.citations} />}
                  {msg.role === 'assistant' && conversationId && (
                    <MessageFeedback conversationId={conversationId} messageIndex={idx} />
                  )}
                  
                  {hasSelectableOptions && (
                    <SelectionButtons
                      options={selectableOptions}
                      onSelect={(option) => handleOptionSelect(option, idx)}
                      disabled={isLoading || selectedMessageIdx === idx}
                    />
                  )}
                  
                  {hasQuestionPrompt && (
                    <GibletQuestionForm
                      content={msg.content}
                      onSubmit={(answers) => handleFormSubmit(answers, idx)}
                      isSubmitted={isFormSubmitted}
                    />
                  )}
                </div>
              </div>
            );
            })}
            {messages.length === 0 && (
              <div className="flex justify-center items-center h-32">
                <p className="text-muted-foreground text-sm">Start a conversation with {variant}</p>
              </div>
            )}
            <div ref={scrollRef} />
          </div>
        </ScrollArea>

      {/* Input Area */}
      <div className="space-y-2 p-3 border-t">
        <div className="flex gap-2">
          <Textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSend();
              }
            }}
            placeholder={`Ask ${variant}...`}
            className="min-h-[200px] max-h-[320px] resize-none text-sm"
            disabled={isLoading}
          />
          <Button
            onClick={() => handleSend()}
            disabled={!input.trim() || isLoading}
            size="sm"
            className="self-end"
          >
            {isLoading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Send className="h-4 w-4" />
            )}
          </Button>
        </div>

        <KBConnectionPanel
          connectedKBs={connectedKBs}
          onConnect={connectKB}
          onDisconnect={disconnectKB}
          canConnectMore={canConnectMore}
          recentlyUsed={getRecentlyUsed()}
          maxConnectedKBs={maxConnectedKBs}
        />

        {/* Chronological History */}
        {userId && (
          <GibletChronologicalHistory
            userId={userId}
            currentVariant={variantKey}
            expanded={showChronological}
            onToggle={() => setShowChronological(!showChronological)}
          />
        )}
        </div>
      </div>
    </>
  );
};
